import clsx from "clsx";
import React, { useRef } from "react";
import { useDropdown } from "../../components/dropdown/useDropdown";
import useTrackDropdownKey from "../../components/dropdown/useTrackDropdownKey";
import { useScrollToSelected } from "../../components/dropdown/useScrollToSelected";
import { dropdownItemsType, dropdownItemType } from "../../types/common";
import { FieldError } from "react-hook-form";

interface DropdownProps {
	additionalClasses?: string;
	dropdownItems: dropdownItemsType;
	dropdownToggle: string;
	error?: FieldError;
	label: string;
	onDropdownItemClick: (choice: dropdownItemType) => void;
	preselected?: boolean;
	required?: boolean;
	scrollToView?: boolean;
}

export function Dropdown({
	additionalClasses,
	dropdownItems,
	dropdownToggle,
	error,
	label,
	onDropdownItemClick,
	preselected = false,
	required = false,
	scrollToView = false,
}: DropdownProps) {
	const {
		dropdownRef,
		dropdownIsOpened,
		onDropdownToggle,
		onDropdownItemWasSelected,
	} = useDropdown();

	const listRef = useRef(null);
	useTrackDropdownKey(dropdownIsOpened, listRef);
	useScrollToSelected(dropdownIsOpened, scrollToView, listRef);

	const handleDropdownItemClick = (choice: dropdownItemType) => {
		onDropdownItemClick(choice);
		onDropdownItemWasSelected();
		onDropdownToggle();
	};

	const items = dropdownItems.map((item) => {
		const { key, value } = item;
		return (
			<li key={key}>
				<button
					type="button"
					className="dropdown-item"
					onClick={() => handleDropdownItemClick(item)}
				>
					{value}
				</button>
			</li>
		);
	});

	const dropdownClasses = clsx(
		"form-group",
		"dropdown dropdown--labelled",
		additionalClasses,
		{
			"dropdown--scrollable": dropdownItems.length > 7,
			error,
			required,
		},
	);

	return (
		<div
			ref={dropdownRef}
			className={dropdownClasses}
			data-selected={preselected}
		>
			<span className="dropdown-label" onClick={onDropdownToggle}>
				{label}
			</span>

			{error && error.type === "required" && (
				<span className="error-text" role="alert">
					This field is required.
				</span>
			)}

			<button
				type="button"
				className="dropdown-toggle"
				aria-expanded={dropdownIsOpened}
				onClick={onDropdownToggle}
			>
				<span>{dropdownToggle}</span>
			</button>

			<div
				className={clsx("dropdown-menu", {
					"dropdown-menu--opened": dropdownIsOpened,
				})}
			>
				<ul ref={listRef} className="dropdown-menu-items">
					{items}
				</ul>
			</div>
		</div>
	);
}
